import { useRef, type MouseEvent, type PointerEvent } from 'react';
import { hapticTap } from './native.js';

const HOLD_MS = 460;
const SLOP_PX = 10;

/**
 * Segurar um item da lista abre as ações dele. Arrastar (rolar a lista) cancela; depois
 * de segurar, o toque não vale como clique (a conversa não abre por engano).
 */
export function useLongPress<T>(onHold: (item: T) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  };

  return (item: T) => ({
    onPointerDown: (event: PointerEvent) => {
      if (event.button !== 0) return;
      fired.current = false;
      start.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        fired.current = true;
        timer.current = null;
        hapticTap();
        onHold(item);
      }, HOLD_MS);
    },
    onPointerMove: (event: PointerEvent) => {
      const from = start.current;
      if (!from) return;
      if (Math.hypot(event.clientX - from.x, event.clientY - from.y) > SLOP_PX) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu: (event: MouseEvent) => {
      // Clique direito (computador) também abre as ações; no celular, some o menu do sistema.
      event.preventDefault();
      if (!fired.current) {
        fired.current = true;
        cancel();
        onHold(item);
      }
    },
    onClickCapture: (event: MouseEvent) => {
      if (!fired.current) return;
      event.preventDefault();
      event.stopPropagation();
      fired.current = false;
    },
  });
}
